// native
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

// addon
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class ErrorService {
  constructor(
    private toastService: ToastrService,
    private translateService: TranslateService
  ) { }

  handleError(errorText?: string) {
    if (errorText && (typeof errorText === 'string'))
      return this.toastService.error(errorText);

    this.toastService.error(this.translateService.instant('somethingWentWrong'));
  }

  handleDeviceError(error: any) {
    if (error && error[0] === 'This device name was previously used in your office group, please choose a different one.')
      return this.toastService.error(this.translateService.instant('deviceDuplicatedErrorMessage'));

    if (error === 'UNLINK_WEEKLY_LIMIT_REACHED')
      return this.toastService.error(this.translateService.instant('deviceUnlinkLimitError'));

    this.handleError(error?.error);
  }

  handleOfficeError(error: any) {
    if (error?.code === 'ACTIVE_ASSETS_EXIST') {
      let message = this.translateService.instant('officeDeletionErrorBase');

      if (error.parameters?.number_of_devices)
        message = message.concat(`<br>${this.translateService.instant('officeDeletionErrorDevice')} <b>${error.parameters?.number_of_devices}</b>`);
      if (error.parameters?.number_of_employees)
        message = message.concat(`<br>${this.translateService.instant('officeDeletionErrorEmployee')} <b>${error.parameters?.number_of_employees}</b>`);

      return this.toastService.error(message, null, { enableHtml: true });
    }

    this.handleError(error);
  }

  handlePatientError(error: any) {
    if (error?.code === 'ACTIVE_WORK_EXISTS') {
      let message = this.translateService.instant('patientDeletionErrorBase');

      // parameters arrive as strings, so coerce before testing for zero
      if (Number(error.parameters?.number_of_tests))
        message = message.concat(`<br>${this.translateService.instant('patientDeletionErrorTests')} <b>${error.parameters?.number_of_tests}</b>`);
      if (Number(error.parameters?.number_of_schedules))
        message = message.concat(`<br>${this.translateService.instant('patientDeletionErrorSchedules')} <b>${error.parameters?.number_of_schedules}</b>`);

      return this.toastService.error(message, null, { enableHtml: true });
    }

    this.handleError(error);
  }

  handleTestError(error: any) {
    if (!error)
      this.handleError();

    if (error.test_descriptions) {
      if (error.test_descriptions.some(desc => desc === 'test_descriptions must contain no more than 10 items.'))
        return this.toastService.error(this.translateService.instant('bundleCreationFailed'));
    }

    if (error.non_field_errors) {
      if (error.non_field_errors.some(desc => desc === 'Cannot create more than 10 custom test bundle types.'))
        return this.toastService.error(this.translateService.instant('customBundleCreationFailed'));

      if (error.non_field_errors.some(desc => desc === 'Invalid fixed exam date'))
        return this.toastService.error(this.translateService.instant('fixedExamDateTestCreationError'));
    }

    return this.toastService.error(this.translateService.instant('testCreationErrorGeneric'));
  }

  handleUserError(form: UntypedFormGroup, error: any) {
    // errors creating new user
    if (error?.username?.includes('This username is taken, please use another one.'))
      form.get('username').setErrors({ usernameTaken: true });

    if (error?.email?.includes('Enter a valid email address.'))
      form.get('email').setErrors({ email: true });

    if (error?.email?.includes('This email address is taken, please use another one.'))
      form.get('email').setErrors({ emailTaken: true });

    if (error?.phone_number?.includes('This field must be unique.') || error?.phone_number?.includes('employee with this phone number already exists.'))
      form.get('phone_number').setErrors({ phoneTaken: true });

    if (error?.sso_id?.includes('This SSO Employee ID is taken.')) {
      form.get('sso_id')?.setErrors({ ssoIdTaken: true });
      form.get('sso_id')?.markAsTouched();
    }

    // errors editing existing user
    if (Array.isArray(error) && error.includes('Username must be unique.'))
      form.get('username').setErrors({ usernameTaken: true });

    if (Array.isArray(error) && error.includes('Email must be unique.'))
      form.get('email').setErrors({ emailTaken: true });

    if (typeof error?.error === 'string' && error.error.includes('Unable to change the role: Doctor is in use.'))
      return this.toastService.error(this.translateService.instant('doctorRoleChangeError'));

    if (typeof error?.error === 'string' && error.error.includes('Unable to change the role: Operator is in use.'))
      return this.toastService.error(this.translateService.instant('operatorRoleChangeError'));

    this.toastService.error(this.translateService.instant('userSettingsValidationGeneric'));
  }

  handleSupportUserError(error: string) {
    if (error === 'EMAIL_IN_USE')
      return this.toastService.error(this.translateService.instant('emailInUse'));

    if (error === 'OGA_UPDATES_WEEKLY_LIMIT_REACHED')
      return this.toastService.error(this.translateService.instant('ogaUpdateLimitError'));

    if (error === 'USER_EMAIL_UPDATES_WEEKLY_LIMIT_REACHED')
      return this.toastService.error(this.translateService.instant('userEmailUpdateLimitError'));

    if (error === 'EMPLOYEE_DESTROY_WEEKLY_LIMIT_REACHED')
      return this.toastService.error(this.translateService.instant('employeeDestroyLimitError'));

    if (error === 'ACTOR_ACTION_IN_PROGRESS')
      return this.toastService.error(this.translateService.instant('actionInProgressError'));

    this.handleError(error);
  }

  handleResetPasswordError(error: string, fallbackKey: string) {
    if (error?.startsWith('resetPassword')) {
      const translated = this.translateService.instant(error);
      if (translated !== error) {
        return this.toastService.error(translated);
      }
    }

    return this.handleError(this.translateService.instant(fallbackKey));
  }

  handleInvitationError(form: UntypedFormGroup, error: any) {
    if (error?.email?.includes('This email address is taken, please use another one.'))
      form.get('email').setErrors({ emailTaken: true });

    this.toastService.error(this.translateService.instant('invitationSentErrorMessage'));
  }

  handleAuthErrors(error: string) {
    if (error === 'sso_required')
      return this.toastService.error(this.translateService.instant('ssoRequiredErrorMessage'));

    if (error && error.includes && error.includes('User login is blocked, try again in'))
      return this.toastService.error(this.translateService.instant('loginBlockedErrorMessage'));

    if (['Duo authentication failed.', '2FA verification failed. Contact you administrator.', '2FA Unavailable. Contact you administrator.'].includes(error))
      return this.toastService.error(this.translateService.instant('2faFailedErrorMessage'));

    if (error === 'WebAuthn authentication failed.')
      return this.toastService.error(this.translateService.instant('deviceAuthFailedErrorMessage'));

    if (['unauthorized', 'fail'].includes(error))
      return this.handleError(null);

    return this.handleError(null);
  }

  handleServerErrors(error: string) {
    if (['Duo authentication failed.', '2FA verification failed. Contact you administrator.', '2FA Unavailable. Contact you administrator.'].includes(error))
      return this.toastService.error(this.translateService.instant('2faFailedErrorMessage'));

    if (error === 'An error occurred during report generation. Please try again later.')
      return this.toastService.error(this.translateService.instant('reportGenerationErrorMessage'));

    if (error === 'Error during test creation')
      return this.toastService.error(this.translateService.instant('testCreationErrorGeneric'));

    return this.handleError(null);
  }
}